import type { TFunction } from 'i18next';
import BuildIcon from '@mui/icons-material/Build';
import { formatDuration } from '@/utils/duration';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';

interface WorkOrderItemsTableProps {
  items: WorkOrderDetail['items'];
  t: TFunction;
  formatPrice: (cents: number) => string;
}

type ItemRow = WorkOrderDetail['items'][number] & {
  offerId?: string | null;
};

function itemOfferId(item: WorkOrderDetail['items'][number]): string | null {
  const offerId = (item as ItemRow).offerId;
  return typeof offerId === 'string' && offerId.length > 0 ? offerId : null;
}

function isDeclinedItemStatus(status: string): boolean {
  return status === 'declined' || status === 'rejected';
}

function isAwaitingCustomer(item: WorkOrderDetail['items'][number]): boolean {
  return itemOfferId(item) != null && item.status === 'pending' && item.approvalRequired;
}

export function WorkOrderItemsTable({
  items,
  t,
  formatPrice,
}: WorkOrderItemsTableProps) {
  if (items.length === 0) {
    return <Typography color="text.secondary">{t('detail.noItems')}</Typography>;
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('detail.colDescription')}</TableCell>
            <TableCell>{t('statusLabel')}</TableCell>
            <TableCell align="right">{t('parts')}</TableCell>
            <TableCell align="right">{t('labor')}</TableCell>
            <TableCell align="right">{t('total')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow
              key={item.id}
              sx={{
                '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                '& > .MuiTableCell-root': { borderBottomColor: 'divider' },
              }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BuildIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {item.description}
                    </Typography>
                    {item.estimatedHours && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDuration(item.estimatedHours)}
                        {item.laborCostCents > 0 ? ` · ${formatPrice(item.laborCostCents)}` : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={
                    isAwaitingCustomer(item)
                      ? t('offers.awaitingCustomer')
                      : item.status === 'completed'
                        ? t('status.completed')
                        : item.status === 'approved'
                          ? t('detail.itemApproved')
                          : isDeclinedItemStatus(item.status)
                            ? t('detail.itemRejected')
                            : t('detail.itemPending')
                  }
                  color={
                    isAwaitingCustomer(item)
                      ? 'warning'
                      : item.status === 'completed'
                        ? 'success'
                        : item.status === 'approved'
                          ? 'info'
                          : isDeclinedItemStatus(item.status)
                            ? 'error'
                            : 'default'
                  }
                />
              </TableCell>
              <TableCell align="right">{formatPrice(item.partsCostCents)}</TableCell>
              <TableCell align="right">{formatPrice(item.laborCostCents)}</TableCell>
              <TableCell align="right">{formatPrice(item.partsCostCents + item.laborCostCents)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
